// src/components/admin/PlatformOverview.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Skeleton,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import BugReportIcon from "@mui/icons-material/BugReport";
import PublicIcon from "@mui/icons-material/Public";
import TokenIcon from "@mui/icons-material/Token";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useTranslation } from "../../i18n";
import { fetchData } from "../../api/apiHelpers";
import SyncRows from "./SyncRows";
import { glass } from "../../theme/glassTokens";

interface OverviewData {
  users: { total: number; active_30d: number };
  organizations: { total: number; paid: number; trialing: number };
  tickets: { open: number; critical: number };
  visitors: { total_30d: number; unique_30d: number };
  usage: { tokens_30d: number; cost_30d: number };
}

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color, loading }) => {
  // contentText.secondary is blueGray and text.secondary is royalBlue: both are
  // tuned for a white card and are buried on this one.
  const textSecondary = glass.textSecondary;
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: 2,
        background: glass.panel,
        border: `1px solid ${glass.panelBorder}`,
        backdropFilter: "blur(8px)",
        transition: "box-shadow 0.2s, border-color 0.2s",
        "&:hover": {
          borderColor: color,
          boxShadow: `0 4px 24px ${alpha(color, 0.12)}`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="body2" sx={{ color: textSecondary }} fontWeight={500}>
            {title}
          </Typography>
          {loading ? (
            <Skeleton width={80} height={40} />
          ) : (
            <Typography variant="h4" fontWeight={700} sx={{ color, mt: 0.5 }}>
              {typeof value === "number" ? value.toLocaleString() : value}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="caption" sx={{ color: textSecondary }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            background: alpha(color, 0.08),
            color,
            display: "flex",
          }}
        >
          {icon}
        </Box>
      </Box>
    </Box>
  );
};

const PlatformOverview: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const [overview, analytics] = await Promise.all([
          fetchData<Record<string, unknown>>("/billing/admin/overview/"),
          fetchData<Record<string, unknown>>("/billing/admin/landing-analytics/?days=30"),
        ]);

        setData({
          users: {
            total: (overview as any).total_users || 0,
            active_30d: (overview as any).active_users_30d || 0,
          },
          organizations: {
            total: (overview as any).total_organizations || 0,
            paid: (overview as any).paid_organizations || 0,
            trialing: (overview as any).trialing_organizations || 0,
          },
          tickets: {
            open: (overview as any).open_tickets || 0,
            critical: (overview as any).critical_tickets || 0,
          },
          visitors: {
            total_30d: (analytics as any).total_views || 0,
            unique_30d: (analytics as any).unique_visitors || 0,
          },
          usage: {
            tokens_30d: (overview as any).tokens_30d || 0,
            cost_30d: (overview as any).cost_30d || 0,
          },
        });
      } catch {
        // Individual endpoint failures are non-critical for overview
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, []);

  return (
    <Box>
      {/* Data syncs at the top — Joan, 2026-05-27 */}
      <SyncRows />

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("admin.stat_total_visitors", "Landing Visitors (30d)")}
            value={data?.visitors.total_30d ?? 0}
            subtitle={`${data?.visitors.unique_30d ?? 0} unique`}
            icon={<PublicIcon />}
            color={glass.accent}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("admin.stat_total_users", "Total Users")}
            value={data?.users.total ?? 0}
            subtitle={`${data?.users.active_30d ?? 0} active this month`}
            icon={<PeopleIcon />}
            color={glass.accent}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("admin.stat_organizations", "Organizations")}
            value={data?.organizations.total ?? 0}
            subtitle={`${data?.organizations.paid ?? 0} paid, ${data?.organizations.trialing ?? 0} trialing`}
            icon={<BusinessIcon />}
            color={theme.palette.success.light}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("admin.stat_open_tickets", "Open Error Tickets")}
            value={data?.tickets.open ?? 0}
            subtitle={data?.tickets.critical ? `${data.tickets.critical} critical` : undefined}
            icon={<BugReportIcon />}
            color={data?.tickets.open ? theme.palette.error.light : theme.palette.success.light}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("admin.stat_tokens", "Tokens Used (30d)")}
            value={data?.usage.tokens_30d ?? 0}
            icon={<TokenIcon />}
            color={theme.palette.secondary.main}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t("admin.stat_cost", "API Cost (30d)")}
            value={data?.usage.cost_30d ? `€${data.usage.cost_30d.toFixed(2)}` : "€0.00"}
            icon={<TrendingUpIcon />}
            color={theme.palette.warning.main}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Quick status strip */}
      <Box sx={{ mt: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Chip
          size="small"
          label={t("admin.system_healthy", "System Healthy")}
          color="success"
          variant="outlined"
        />
        <Chip
          size="small"
          label={t("admin.geoip_provider", "GeoIP: ipwho.is (free)")}
          variant="outlined"
        />
      </Box>
    </Box>
  );
};

export default PlatformOverview;
